/**
 * Sugerencias de términos a unificar en un listado.
 *
 * Solo se agrupan términos que cuelgan del mismo padre: dos «Otros» bajo categorías
 * distintas no son el mismo término. Cada grupo es un array de ids; los términos se
 * reciben como { id, label, parentId }.
 */

// Minúsculas, sin acentos, sin espacios ni signos: «Virgin Islands, British» y
// «virginislandsbritish» quedan iguales
export function compactLabel(label) {
  return String(label || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "");
}

// Palabras ordenadas: «Islands, Virgin» y «Virgin Islands» quedan iguales
function sortedWords(label) {
  return String(label || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .sort()
    .join(" ");
}

function levenshtein(a, b, maxDistance) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > maxDistance) return maxDistance + 1;
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
      if (current[j] < rowMin) rowMin = current[j];
    }
    if (rowMin > maxDistance) return maxDistance + 1;
    previous = current;
  }
  return previous[b.length];
}

/**
 * Parecido entre 0 y 1 de dos etiquetas (1 = iguales una vez normalizadas).
 */
export function labelSimilarity(a, b) {
  const ca = compactLabel(a);
  const cb = compactLabel(b);
  if (!ca || !cb) return 0;
  if (ca === cb || sortedWords(a) === sortedWords(b)) return 1;
  const longest = Math.max(ca.length, cb.length);
  return 1 - levenshtein(ca, cb, longest) / longest;
}

function groupByParent(terms) {
  const byParent = new Map();
  terms.forEach((term) => {
    const key = term.parentId || "";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(term);
  });
  return [...byParent.values()];
}

/**
 * Mismo nombre ignorando mayúsculas, acentos, espacios y signos.
 */
export function buildSameNameGroups(terms) {
  const groups = [];
  groupByParent(terms).forEach((siblings) => {
    const byKey = new Map();
    siblings.forEach((term) => {
      const key = compactLabel(term.label);
      if (!key) return;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(term.id);
    });
    byKey.forEach((ids) => {
      if (ids.length > 1) groups.push(ids);
    });
  });
  return groups;
}

/**
 * Nombres parecidos: une en un mismo grupo los términos cuyo parecido supera el umbral,
 * de forma transitiva (si A≈B y B≈C, van juntos). Las palabras de menos de 3 letras
 * no se comparan por distancia: «UK» y «US» no son el mismo país.
 */
export function buildSimilarNameGroups(terms, threshold = 0.85) {
  const groups = [];
  groupByParent(terms).forEach((siblings) => {
    const items = siblings
      .map((term) => ({ id: term.id, label: term.label, compact: compactLabel(term.label) }))
      .filter((item) => item.compact);
    const parent = items.map((_, i) => i);
    const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
    const union = (i, j) => {
      parent[find(i)] = find(j);
    };

    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i];
        const b = items[j];
        if (a.compact === b.compact) {
          union(i, j);
          continue;
        }
        if (Math.min(a.compact.length, b.compact.length) < 3) continue;
        const longest = Math.max(a.compact.length, b.compact.length);
        if (Math.abs(a.compact.length - b.compact.length) / longest > 1 - threshold) {
          // Por largo ya no pueden llegar al umbral; solo queda que sean las mismas palabras
          if (sortedWords(a.label) === sortedWords(b.label)) union(i, j);
          continue;
        }
        if (labelSimilarity(a.label, b.label) >= threshold) union(i, j);
      }
    }

    const byRoot = new Map();
    items.forEach((item, i) => {
      const root = find(i);
      if (!byRoot.has(root)) byRoot.set(root, []);
      byRoot.get(root).push(item.id);
    });
    byRoot.forEach((ids) => {
      if (ids.length > 1) groups.push(ids);
    });
  });
  return groups;
}
